import { ButtonHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'warning'
  size?: 'sm' | 'md' | 'lg' | 'xl'
  fullWidth?: boolean
}

// Touch-optimized button for Galaxy Tab Active5
// Nordic Industrial palette - Minimum 48px height, high contrast, large text
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    children, 
    variant = 'primary', 
    size = 'lg', 
    fullWidth = false, 
    className, 
    disabled,
    ...props 
  }, ref) => {
    const baseStyles = 'font-semibold rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed no-select'
    
    const variants = {
      primary: 'bg-[#1E3A5F] text-white hover:bg-[#152C4A] active:bg-[#0F1F35] shadow-sm',
      secondary: 'bg-white text-slate-700 hover:bg-slate-50 active:bg-slate-100 border border-slate-300 hover:border-slate-400',
      success: 'bg-emerald-600 text-white hover:bg-emerald-700 active:bg-emerald-800 shadow-sm',
      danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800 shadow-sm',
      warning: 'bg-amber-500 text-slate-900 hover:bg-amber-600 active:bg-amber-700 shadow-sm',
    }
    
    const sizes = {
      sm: 'px-4 py-2 text-base min-h-[48px]',
      md: 'px-6 py-3 text-lg min-h-[56px]',
      lg: 'px-8 py-4 text-xl min-h-[64px]',
      xl: 'px-10 py-5 text-2xl min-h-[72px]',
    }
    
    return (
      <button
        ref={ref}
        className={clsx(
          baseStyles,
          variants[variant],
          sizes[size],
          fullWidth && 'w-full',
          className
        )}
        disabled={disabled}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button

